import * as Nebula from 'three-nebula';
import common from '../common';

export default class THREEParticleEmitter extends THREE.Group {
	constructor(json, state = true) {
		super();

		this._system = null;
		this._emitter = null;
		this._texture = null;
		this._sprite = null;
		this._blendMode = 1;
		this._emitterData = json;
		this._dead = false;
		this._active = state;
		this._emitterSettings = this._emitterData.emitters[0];

		this.signals = {
			onStart   : new P3.utils.MiniSignal(),
			onEnd     : new P3.utils.MiniSignal(),
			onDispose : new P3.utils.MiniSignal()
		};

		this.load(json);
	}

	load(json) {
		// eslint-disable-next-line new-cap
		this._blendMode = this._emitterSettings.blendMode;
		this._system = new Nebula.System.fromJSON(json, THREE);
		this._emitter = this.createEmitter(0);
		this._system.renderer = new Nebula.SpriteRenderer(this, THREE);
		this._system.addRenderer(this._system.renderer);

		this.onPosition(this._emitterSettings.position);
		this.onZone(this.getData('initializers', 'Position'));
		this.onRate(this._emitterSettings.rate);
		this.onLife(this.getData('initializers', 'Life'));
		this.onVelocity(this.getData('initializers', 'RadialVelocity'));
		this.onColor(this.getData('behaviours', 'Color'));
		this.onAlpha(this.getData('behaviours', 'Alpha'));
		this.onRadius(this.getData('initializers', 'Radius'));
		this.onScale(this.getData('behaviours', 'Scale'));
		this.onMass(this.getData('initializers', 'Mass'));
		this.onForce(this.getData('behaviours', 'Force'));

		if (!this._active) {
			this.stop(false);
		}
	}

	play(count = Infinity, kill = true) {
		const time = count === Infinity ? count : this._emitterSettings.initializers.find((e) => {
			return e.type === 'Life';
		}).properties.max * 1000;

		this._emitter.emit(count + 1, time);
		this.signals.onStart.dispatch();
		this._active = true;

		if (count < Infinity) {
			setTimeout(() => {
				this.stop(kill);
			}, ((count + 2) * time) * 1000);
		}
	}

	playDuration(duration = 0.1, kill = true) {
		this._emitter.emit();
		this._active = true;
		this.signals.onStart.dispatch();

		setTimeout(() => {
			this.stop(kill);
		}, duration * 1000);
	}

	stop(kill = false) {
		this._emitter.stopEmit();
		this.signals.onEnd.dispatch();

		if (kill) {
			this.dispose();
		}
	}

	createEmitter(emitterIndex) {
		const emitter = this._system.emitters[emitterIndex];

		emitter.removeInitializer(this.getInitializer('Texture', emitter));
		const bodySpriteInitializer = this.getInitializer('BodySprite', this._emitterSettings);
		const texturename = this.getTextureName(bodySpriteInitializer.properties.texture);

		emitter.addInitializers([
			new Nebula.Body(this.createBody(common.assets.texture(texturename)))
		]);

		return emitter;
	}

	createBody(source) {
		if (this._texture !== null && this.textureName !== null) {
			this._texture.dispose();
		}

		if (typeof source === 'string') {
			this._texture = new THREE.TextureLoader().load(source);
		} else {
			this._texture = source;
		}

		const material = new THREE.SpriteMaterial({
			map      : this._texture,
			blending : THREE.CustomBlending
		});

		material.blendSrc = THREE.OneFactor;				// https://stackoverflow.com/a/36524892/2992183
		material.blendDst = THREE.OneMinusSrcAlphaFactor;

		this._sprite = new THREE.Sprite(material);

		return this._sprite;
	}

	getInitializer(type, emitter = this._emitter) {
		return emitter.initializers.find((element) => {
			return element.type === type;
		});
	}

	getBehaviour(type, emitter = this._emitter) {
		return emitter.behaviours.find((element) => {
			return element.type === type;
		});
	}

	getData(group = 'initializers', type, source = this._emitterSettings) {
		for (let i = 0; i < source[group].length; i++) {
			const initializer = source[group][i];

			if (initializer.type === type) {
				return initializer.properties;
			}
		}

		return null;
	}

	onPosition(parent) {
		if (!this._emitter) {
			return;
		}

		this._emitter.position.copy(parent);
	}

	onRate(parent) {
		if (!this._emitter) {
			return;
		}

		const pMin = parent.particlesMin;
		const pMax = parent.particlesMax;
		const tMin = parent.perSecondMin;
		const tMax = parent.perSecondMax;

		this._emitter.setRate(new Nebula.Rate(this.span(pMin, pMax), this.span(tMin, tMax)));
	}

	onZone(parent) {
		if (!this._emitter) {
			return;
		}

		const position = this.getInitializer('Position');

		position.zones = [];

		switch (parent.zoneType) {
			// point
			case 'PointZone':
				position.zones.push(new Nebula.PointZone(0, 0, 0));

				break;

				// sphere
			case 'SphereZone':
				position.zones.push(new Nebula.SphereZone(0, 0, 0, parent.radius));

				break;

				// box
			case 'BoxZone':
				position.zones.push(new Nebula.BoxZone(0, 0, 0, parent.w, parent.h, parent.d));

				break;
		}
	}

	onLife(parent) {
		if (!this._emitter) {
			return;
		}

		const initializer = this.getInitializer('Life');

		initializer.lifePan = this.span(parent.min, parent.max);
	}

	onVelocity(parent) {
		if (!this._emitter) {
			return;
		}

		const initializer = this.getInitializer('RadialVelocity');

		initializer.radiusPan = this.span(parent.radius);
		initializer.dir = new Nebula.Vector3D(parent.x, parent.y, parent.z);
		initializer.tha = (parent.theta * Math.PI) / 180;
	}

	onAlpha(parent) {
		if (!this._emitter) {
			return;
		}

		const behaviour = this.getBehaviour('Alpha');

		behaviour.alphaA = this.span(parent.alphaA, parent.alphaA);
		behaviour.alphaB = this.span(parent.alphaB, parent.alphaB);
	}

	onColor(parent) {
		if (!this._emitter) {
			return;
		}

		const behaviour = this.getBehaviour('Color');

		behaviour.colorA = new Nebula.ColorSpan([parent.colorA]);
		behaviour.colorB = new Nebula.ColorSpan([parent.colorB]);
	}

	onRadius(parent) {
		if (!this._emitter) {
			return;
		}

		this.getInitializer('Radius').radius = this.span(parent.width, parent.height);
	}

	onScale(parent) {
		if (!this._emitter) {
			return;
		}

		const behaviour = this.getBehaviour('Scale');

		behaviour.isEnabled = parent.isEnabled;
		behaviour.scaleA = this.span(parent.scaleA, parent.scaleA);
		behaviour.scaleB = this.span(parent.scaleB, parent.scaleB);
		behaviour.easing = Nebula.ease[parent.easing];
	}

	onMass(parent) {
		if (!this._emitter) {
			return;
		}

		const initializer = this.getInitializer('Mass');

		initializer.isEnabled = parent.isEnabled;
		initializer.massPan = this.span(parent.min, parent.max);
	}

	onForce(parent) {
		if (!this._emitter) {
			return;
		}

		const behaviour = this.getBehaviour('Force');

		behaviour.isEnabled = parent.isEnabled;
		behaviour.force.x = parent.fx;
		behaviour.force.y = parent.fy;
		behaviour.force.z = parent.fz;
	}

	span(min, max) {
		return new Nebula.Span(min, max);
	}

	dispose() {
		this._active = false;
		this._dead = true;
		this._sprite.geometry.dispose();
		this._sprite.material.dispose();
		this._system.destroy();
		this.signals.onDispose.dispatch();
	}

	update() {
		if (!this._system || this._dead || !this._active) {
			return;
		}
		this._system.update();
	}

	getTextureName(filename) {
		return filename.substring(filename.lastIndexOf('/') + 1, filename.lastIndexOf('.')) || filename;
	}

	get dead() {
		return this._dead;
	}

	get active() {
		return this._active;
	}

	get sprite() {
		return this._sprite;
	}

	set sprite(v) {
		this._sprite = v;
	}
}
